import Phaser from 'phaser';
import { CustomLevelManager, CustomLevel } from '../systems/CustomLevelManager';
import { LevelLoader } from '../systems/LevelLoader';
import { TilePosition, PathDef, TreeSlotDef, RegionType, WaveDef, SpawnDef, BiomeType, ScenarioDef, BossDef } from '../systems/LevelLoader';
import { DESIGN_WIDTH, DESIGN_HEIGHT, COLORS } from '../constants';
import { InputRouter } from '../systems/InputRouter';
import { GameState } from '../state/GameState';
import { MonsterType, Monster } from '../entities/Monster';
import { TowerType, Tower } from '../entities/Tower';

// ============================================
// Map Editor Scene
// Create and edit custom maps
// ============================================

type EditorTool = 'select' | 'base' | 'path' | 'tree' | 'spawn' | 'erase';
type EditorMode = 'menu' | 'editor';

// 16:9 aspect ratio with double granularity (48x27 tiles)
const DEFAULT_MAP_WIDTH = 48;
const DEFAULT_MAP_HEIGHT = 27;
const GRID_TILE_SIZE = 32;  // Larger tiles for better visibility
const MAP_OFFSET_X = 240;   // Space for left panel (230px wide)
const MAP_OFFSET_Y = 55;    // Space for top bar

const TOOL_COLORS: Record<EditorTool, number> = {
  select: 0x888888,
  base: 0x44ff44,
  path: 0xffaa00,
  tree: 0x228822,
  spawn: 0x4488ff,
  erase: 0xff4444,
};

const BOSS_TYPES: Array<{ id: string; label: string }> = [
  { id: 'brute_boss', label: 'Brute Boss' },
  { id: 'earth_tower', label: 'Earth Tower' },
  { id: 'snail_beast', label: 'Snail Beast' },
  { id: 'giant_bug', label: 'Giant Bug' },
];

export class MapEditorScene extends Phaser.Scene {
  // Current state
  private mode: EditorMode = 'menu';
  private currentLevel: CustomLevel | null = null;
  private currentTool: EditorTool = 'select';
  private isDirty: boolean = false;

  // Path drawing
  private currentPathId: string = 'path-1';
  private isDrawingPath: boolean = false;
  private pathPoints: TilePosition[] = [];
  private pathIdCounter: number = 1;

  // UI elements
  private menuContainer!: Phaser.GameObjects.Container;
  private editorContainer!: Phaser.GameObjects.Container;
  private levelListContainer!: Phaser.GameObjects.Container;
  private gameLevelOverlayContainer!: Phaser.GameObjects.Container;
  private gameLevelListContainer!: Phaser.GameObjects.Container;
  private gameLevelListScrollOffset: number = 0;
  private gameLevelListTop: number = 0;
  private gameLevelListAreaHeight: number = 380;
  private gameLevelListTotalHeight: number = 0;
  private toolbarContainer!: Phaser.GameObjects.Container;
  private propertiesContainer!: Phaser.GameObjects.Container;
  private mapContainer!: Phaser.GameObjects.Container;
  private gridGraphics!: Phaser.GameObjects.Graphics;
  private pathGraphics!: Phaser.GameObjects.Graphics;
  private cursorGraphics!: Phaser.GameObjects.Graphics;

  // Map display (16:9 aspect ratio with double granularity)
  private mapWidth: number = DEFAULT_MAP_WIDTH;
  private mapHeight: number = DEFAULT_MAP_HEIGHT;
  private hoveredTile: TilePosition | null = null;

  // UI buttons/text that need updating
  private toolButtons: Map<EditorTool, Phaser.GameObjects.Container> = new Map();
  private pathListItems: Phaser.GameObjects.Container[] = [];
  private pathListStartY = 0;  // set in createLeftPanel; path rows drawn below this
  private spawnCountTexts: Phaser.GameObjects.Text[] = [];
  private levelNameText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private biomeForestButton!: Phaser.GameObjects.Container;
  private biomeBogButton!: Phaser.GameObjects.Container;
  private biomeRuinsButton!: Phaser.GameObjects.Container;
  private biomeVolcanoButton!: Phaser.GameObjects.Container;

  // Wave builder
  private waveBuilderContainer!: Phaser.GameObjects.Container;
  private waveListContainer!: Phaser.GameObjects.Container;
  private waveBuilderVisible: boolean = false;
  private selectedWaveIndex: number = -1;
  private waveBuilderElements: Phaser.GameObjects.GameObject[] = [];
  private scrollOffset: number = 0;
  private maxScroll: number = 0;

  // Tower config panel
  private towerPanelContainer!: Phaser.GameObjects.Container;
  private towerPanelVisible: boolean = false;
  private towerPanelElements: Phaser.GameObjects.GameObject[] = [];

  // Test mode option
  private testWithWaves: boolean = true;  // true = custom waves, false = experiment mode

  constructor() {
    super({ key: 'MapEditorScene' });
  }

  create(): void {
    // Background
    this.add.rectangle(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, DESIGN_WIDTH, DESIGN_HEIGHT, 0x1a1a2e);

    // Create containers
    this.menuContainer = this.add.container(0, 0);
    this.editorContainer = this.add.container(0, 0);
    this.gameLevelOverlayContainer = this.add.container(0, 0);
    this.editorContainer.setVisible(false);
    this.gameLevelOverlayContainer.setVisible(false);

    this.createMenuUI();
    this.createGameLevelOverlay();
    this.setupGameLevelListScroll();
    this.createEditorUI();

    // Input handling
    this.setupInput();
  }

  // ============================================
  // Menu UI (Level Selection)
  // ============================================

  private createMenuUI(): void {
    const centerX = DESIGN_WIDTH / 2;
    const centerY = DESIGN_HEIGHT / 2;

    // Title
    const title = this.add.text(centerX, 60, 'MAP EDITOR', {
      fontSize: '48px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.menuContainer.add(title);

    // Subtitle
    const subtitle = this.add.text(centerX, 110, 'Create and edit custom maps', {
      fontSize: '20px',
      color: '#888888',
    });
    subtitle.setOrigin(0.5);
    this.menuContainer.add(subtitle);

    // Level list panel (content area: 600×500, top at centerY - 210)
    const panelW = 600;
    const panelH = 500;
    const panelBg = this.add.rectangle(centerX, centerY + 40, panelW, panelH, 0x2a2a4e, 0.9);
    panelBg.setStrokeStyle(2, 0x4a4a6e);
    this.menuContainer.add(panelBg);

    // Level list container — positioned inside the panel (list rows sit below panel top + margin)
    const panelTop = centerY + 40 - panelH / 2;
    const listMargin = 24;
    const listContentTop = panelTop + listMargin;
    const listContentLeft = centerX - panelW / 2 + listMargin;
    // Row content: each item draws bg at (280, 25); we want that bg left at listContentLeft, so container x = listContentLeft - 280
    this.levelListContainer = this.add.container(
      listContentLeft - 280,
      listContentTop - 25
    );
    this.menuContainer.add(this.levelListContainer);

    // Bottom buttons: explicit widths so text stays inside (LOAD GAME LEVEL is long)
    const newBtn = this.createButton(centerX - 220, centerY + 280, 'NEW MAP', 0x44aa44, () => {
      this.promptNewLevel();
    }, 140);
    this.menuContainer.add(newBtn);

    const loadGameBtn = this.createButton(centerX, centerY + 280, 'LOAD GAME LEVEL', 0x9944aa, () => {
      this.showGameLevelOverlay();
    }, 200);
    this.menuContainer.add(loadGameBtn);

    const backBtn = this.createButton(centerX + 220, centerY + 280, 'BACK', 0x666666, () => {
      this.scene.start('MapScene');
    }, 100);
    this.menuContainer.add(backBtn);

    // Refresh list
    this.refreshLevelList();
  }

  private createGameLevelOverlay(): void {
    const centerX = DESIGN_WIDTH / 2;
    const centerY = DESIGN_HEIGHT / 2;

    // Dim background
    const bg = this.add.rectangle(centerX, centerY, DESIGN_WIDTH, DESIGN_HEIGHT, 0x000000, 0.75);
    bg.setInteractive({ useHandCursor: false });
    bg.on('pointerdown', () => this.hideGameLevelOverlay());
    this.gameLevelOverlayContainer.add(bg);

    // Panel: taller so list and CANCEL don't overlap
    const panelW = 520;
    const panelH = 580;
    const panelBg = this.add.rectangle(centerX, centerY, panelW, panelH, 0x2a2a4e, 0.98);
    panelBg.setStrokeStyle(2, 0x4a4a6e);
    panelBg.setInteractive({ useHandCursor: false });
    panelBg.on('pointerdown', (e: Phaser.Input.Pointer) => e.event?.stopPropagation());
    this.gameLevelOverlayContainer.add(panelBg);

    const title = this.add.text(centerX, centerY - 265, 'Load from game level', {
      fontSize: '22px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.gameLevelOverlayContainer.add(title);

    const subtitle = this.add.text(centerX, centerY - 232, 'Select a level to use as template (paths, waves, timings, etc.)', {
      fontSize: '14px',
      color: '#aaaaaa',
      align: 'center',
    });
    subtitle.setOrigin(0.5);
    this.gameLevelOverlayContainer.add(subtitle);

    // List container with space above CANCEL (scrollable)
    const listAreaHeight = 380;
    const listLeft = centerX - panelW / 2 + 20;
    const listTop = centerY - 195;
    this.gameLevelListTop = listTop;
    this.gameLevelListAreaHeight = listAreaHeight;
    this.gameLevelListContainer = this.add.container(listLeft, listTop);
    this.gameLevelOverlayContainer.add(this.gameLevelListContainer);

    // Mask so list doesn't draw over CANCEL (no overlay zone - wheel is global, so clicks reach list items)
    const listMask = this.add.graphics().setPosition(listLeft, listTop).setVisible(false);
    listMask.fillStyle(0xffffff, 1);
    listMask.fillRect(0, 0, panelW - 40, listAreaHeight);
    const mask = listMask.createGeometryMask();
    this.gameLevelListContainer.setMask(mask);

    const cancelBtn = this.createButton(centerX, centerY + 258, 'CANCEL', 0x666666, () => {
      this.hideGameLevelOverlay();
    });
    this.gameLevelOverlayContainer.add(cancelBtn);
  }

  private setupGameLevelListScroll(): void {
    this.input.on('wheel', (
      _pointer: Phaser.Input.Pointer,
      _gameObjects: Phaser.GameObjects.GameObject[],
      _deltaX: number,
      deltaY: number
    ) => {
      if (!this.gameLevelOverlayContainer.visible || this.gameLevelListTotalHeight <= this.gameLevelListAreaHeight) return;
      const maxScroll = -Math.max(0, this.gameLevelListTotalHeight - this.gameLevelListAreaHeight);
      this.gameLevelListScrollOffset = Phaser.Math.Clamp(this.gameLevelListScrollOffset - deltaY, maxScroll, 0);
      this.gameLevelListContainer.y = this.gameLevelListTop + this.gameLevelListScrollOffset;
    });
  }

  private showGameLevelOverlay(): void {
    this.gameLevelListScrollOffset = 0;
    this.refreshGameLevelList();
    this.gameLevelListContainer.y = this.gameLevelListTop;
    this.gameLevelOverlayContainer.setVisible(true);
  }

  private hideGameLevelOverlay(): void {
    this.gameLevelOverlayContainer.setVisible(false);
  }

  private refreshGameLevelList(): void {
    this.gameLevelListContainer.removeAll(true);

    const rowWidth = 460;
    const rowHeight = 44;
    const scenarioIds = LevelLoader.getAvailableScenarios().filter((id) => {
      const def = LevelLoader.getScenarioDef(id);
      return def && !(def as CustomLevel).isCustom;
    });
    this.gameLevelListTotalHeight = scenarioIds.length * rowHeight;

    scenarioIds.forEach((scenarioId, index) => {
      const def = LevelLoader.getScenarioDef(scenarioId);
      if (!def) return;

      const y = index * rowHeight;
      const itemContainer = this.add.container(0, y);

      const bg = this.add.rectangle(rowWidth / 2, 18, rowWidth, 40, 0x3a3a5e);
      bg.setStrokeStyle(1, 0x5a5a7e);
      bg.setInteractive({ useHandCursor: true });
      itemContainer.add(bg);

      const name = this.add.text(12, 0, def.name, {
        fontSize: '16px',
        color: '#ffffff',
        fontStyle: 'bold',
      });
      itemContainer.add(name);

      const diffStars = '★'.repeat(Math.min(5, Math.max(0, def.difficulty)));
      const info = this.add.text(12, 20, `Paths: ${def.paths.length} | Waves: ${def.waves.length} | Diff: ${diffStars}`, {
        fontSize: '12px',
        color: '#aaaaaa',
      });
      itemContainer.add(info);

      bg.on('pointerdown', () => {
        this.loadScenarioIntoEditor(scenarioId);
        this.hideGameLevelOverlay();
      });

      this.gameLevelListContainer.add(itemContainer);
    });
  }

  /**
   * Scale a tile position from source map size to editor grid (48x27).
   */
  private scaleTileToEditor(tile: TilePosition, fromW: number, fromH: number): TilePosition {
    const scaleX = DEFAULT_MAP_WIDTH / fromW;
    const scaleY = DEFAULT_MAP_HEIGHT / fromH;
    return {
      x: Math.round(tile.x * scaleX),
      y: Math.round(tile.y * scaleY),
    };
  }

  /**
   * Convert a built-in ScenarioDef to a CustomLevel for editing (does not save).
   * Scales the map to the standard editor grid (48x27).
   * Expands any path that has multiple routes into separate path-1, path-2, ... so the
   * editor lists and waves can show/edit each path; wave spawns are distributed across
   * the new path ids so monsters use different paths.
   */
  private scenarioDefToCustomLevel(scenario: ScenarioDef): CustomLevel {
    const now = Date.now();
    const regionToBiome: Record<RegionType, BiomeType> = {
      forest: 'forest',
      swamp: 'bog',
      ruins: 'ruins',
      volcano: 'volcano',
    };
    const ow = scenario.mapSize.w;
    const oh = scenario.mapSize.h;
    const scaleTile = (t: TilePosition) => this.scaleTileToEditor(t, ow, oh);

    const expandedPaths: PathDef[] = [];
    const pathIdMapping: Record<string, string[]> = {}; // old path id -> [new path ids]

    scenario.paths.forEach((p) => {
      if (p.routes && Object.keys(p.routes).length > 0) {
        // Multi-route path: expand each route into its own path (path-1, path-2, ...)
        const routeEntries = Object.entries(p.routes);
        const newIds: string[] = [];
        routeEntries.forEach(([routeId]) => {
          const pathId = `path-${expandedPaths.length + 1}`;
          newIds.push(pathId);
          const points = (p.routes![routeId] || []).map(scaleTile);
          expandedPaths.push({
            id: pathId,
            points,
            curved: p.curved,
            curveResolution: p.curveResolution,
          });
        });
        pathIdMapping[p.id] = newIds;
      } else if (p.points && p.points.length > 0) {
        // Simple path: one path (path-1, path-2, ... for editor consistency)
        const pathId = `path-${expandedPaths.length + 1}`;
        pathIdMapping[p.id] = [pathId];
        expandedPaths.push({
          id: pathId,
          points: p.points.map(scaleTile),
          curved: p.curved,
          curveResolution: p.curveResolution,
        });
      }
    });

    // Rewrite waves so spawns use the new path ids (distribute across paths when we expanded routes)
    const waves: WaveDef[] = scenario.waves.map((wave) => ({
      ...wave,
      spawns: wave.spawns.map((spawn, spawnIndex) => {
        const newIds = pathIdMapping[spawn.pathId];
        if (!newIds || newIds.length === 0) return spawn;
        const pathId = newIds[spawnIndex % newIds.length];
        return { ...spawn, pathId };
      }),
      boss: wave.boss
        ? (() => {
            const newIds = pathIdMapping[wave.boss.pathId];
            if (!newIds || newIds.length === 0) return wave.boss;
            return { ...wave.boss, pathId: newIds[0] };
          })()
        : undefined,
    }));

    return {
      ...scenario,
      id: `IMPORT_${scenario.id}_${now}`,
      isCustom: true,
      version: 1,
      createdAt: now,
      updatedAt: now,
      hasManualTrees: scenario.treeSlots.length > 0,
      biome: regionToBiome[scenario.region],
      mapSize: { w: DEFAULT_MAP_WIDTH, h: DEFAULT_MAP_HEIGHT },
      tileSize: 45,
      base: scaleTile(scenario.base),
      playerSpawns: scenario.playerSpawns.map(scaleTile),
      treeSlots: scenario.treeSlots.map((t) => ({ ...t, ...scaleTile(t) })),
      paths: expandedPaths,
      waves,
    } as CustomLevel;
  }

  /**
   * Load a built-in game level into the editor (as a new editable copy).
   */
  private loadScenarioIntoEditor(scenarioId: string): void {
    const def = LevelLoader.getScenarioDef(scenarioId);
    if (!def) return;
    if ((def as CustomLevel).isCustom) return; // Only load built-in
    const customLevel = this.scenarioDefToCustomLevel(def as ScenarioDef);
    this.loadLevel(customLevel);
  }

  private refreshLevelList(): void {
    // Clear existing
    this.levelListContainer.removeAll(true);

    const levels = CustomLevelManager.getAllLevels();
    
    if (levels.length === 0) {
      // Center empty message inside the list area (list content width ~540, center at 270 from row start 280)
      const emptyText = this.add.text(550, 200, 'No custom maps yet.\nClick "NEW MAP" to create one!', {
        fontSize: '18px',
        color: '#888888',
        align: 'center',
      });
      emptyText.setOrigin(0.5);
      this.levelListContainer.add(emptyText);
      return;
    }

    // Row width fits inside panel (panel content 552px); row bg at (280, 25)
    const rowWidth = 532;
    levels.forEach((level, index) => {
      const y = index * 70;
      const itemContainer = this.add.container(0, y);

      // Background — fits inside panel
      const bg = this.add.rectangle(280, 25, rowWidth, 60, 0x3a3a5e);
      bg.setStrokeStyle(1, 0x5a5a7e);
      bg.setInteractive({ useHandCursor: true });
      itemContainer.add(bg);

      // Level name
      const name = this.add.text(20, 10, level.name, {
        fontSize: '20px',
        color: '#ffffff',
        fontStyle: 'bold',
      });
      itemContainer.add(name);

      // Info line
      const info = this.add.text(20, 35, 
        `Paths: ${level.paths.length} | Trees: ${level.treeSlots.length} | Diff: ${'★'.repeat(level.difficulty)}`, {
        fontSize: '14px',
        color: '#aaaaaa',
      });
      itemContainer.add(info);

      // Test button - jump straight into experiment mode
      const testBtn = this.createSmallButton(360, 25, 'TEST', 0x44aa44, () => {
        this.testLevelDirectly(level);
      });
      itemContainer.add(testBtn);

      // Edit button
      const editBtn = this.createSmallButton(430, 25, 'EDIT', 0x4488ff, () => {
        this.loadLevel(level);
      });
      itemContainer.add(editBtn);

      // Delete button
      const deleteBtn = this.createSmallButton(500, 25, 'DEL', 0xff4444, () => {
        this.confirmDeleteLevel(level);
      });
      itemContainer.add(deleteBtn);

      // Click to edit
      bg.on('pointerdown', () => {
        this.loadLevel(level);
      });

      this.levelListContainer.add(itemContainer);
    });
  }

  // ============================================
  // Editor UI
  // ============================================

  private createEditorUI(): void {
    // Top bar
    this.createTopBar();

    // Left panel (tools + properties)
    this.createLeftPanel();

    // Map area
    this.createMapArea();

    // Right panel (wave builder)
    this.createWaveBuilderPanel();

    // Tower config panel (overlays on right side)
    this.createTowerPanel();

    // Status bar
    this.statusText = this.add.text(MAP_OFFSET_X + 10, DESIGN_HEIGHT - 30, '', {
      fontSize: '14px',
      color: '#888888',
    });
    this.editorContainer.add(this.statusText);
  }

  private createTopBar(): void {
    const bar = this.add.rectangle(DESIGN_WIDTH / 2, 30, DESIGN_WIDTH, 60, 0x2a2a4e);
    this.editorContainer.add(bar);

    // Level name
    this.levelNameText = this.add.text(20, 20, 'Untitled Map', {
      fontSize: '24px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.editorContainer.add(this.levelNameText);

    // Towers toggle button
    const towersBtn = this.createButton(DESIGN_WIDTH - 720, 30, 'TOWERS', 0xdd8844, () => {
      this.toggleTowerPanel();
    });
    this.editorContainer.add(towersBtn);

    // Waves toggle button
    const wavesBtn = this.createButton(DESIGN_WIDTH - 610, 30, 'WAVES', 0x9944aa, () => {
      this.toggleWaveBuilder();
    });
    this.editorContainer.add(wavesBtn);

    // Load template (replace current map with a game level)
    const templateBtn = this.createButton(DESIGN_WIDTH - 500, 30, 'TEMPLATE', 0x9944aa, () => {
      this.showGameLevelOverlay();
    });
    this.editorContainer.add(templateBtn);

    // Export button - export map as JSON (level-definition format)
    const exportBtn = this.createButton(DESIGN_WIDTH - 390, 30, 'EXPORT', 0xdd8800, () => {
      this.exportCurrentMap();
    });
    this.editorContainer.add(exportBtn);

    // Save button
    const saveBtn = this.createButton(DESIGN_WIDTH - 280, 30, 'SAVE', 0x44aa44, () => {
      this.saveCurrentLevel();
    });
    this.editorContainer.add(saveBtn);

    // Test Play button - with dropdown options
    const testBtn = this.createButton(DESIGN_WIDTH - 170, 30, 'TEST ▼', 0x4488ff, () => {
      this.showTestOptions();
    });
    this.editorContainer.add(testBtn);

    // Exit button
    const exitBtn = this.createButton(DESIGN_WIDTH - 60, 30, 'EXIT', 0x666666, () => {
      this.exitEditor();
    });
    this.editorContainer.add(exitBtn);
  }

  private createLeftPanel(): void {
    // Panel: left-aligned with padding so labels/buttons aren't cut off
    const panelWidth = 230;
    const padding = 18;
    const panelCenterX = padding + panelWidth / 2;
    const panelBg = this.add.rectangle(panelCenterX, DESIGN_HEIGHT / 2 + 25, panelWidth, DESIGN_HEIGHT - 80, 0x2a2a4e);
    panelBg.setStrokeStyle(1, 0x4a4a6e);
    this.editorContainer.add(panelBg);

    // Tools section
    const toolsLabel = this.add.text(padding, 70, 'TOOLS', {
      fontSize: '16px',
      color: '#888888',
      fontStyle: 'bold',
    });
    this.editorContainer.add(toolsLabel);

    this.toolbarContainer = this.add.container(padding, 95);
    this.editorContainer.add(this.toolbarContainer);

    const tools: { tool: EditorTool; label: string; shortcut: string }[] = [
      { tool: 'select', label: 'Sel', shortcut: 'V' },
      { tool: 'base', label: 'Base', shortcut: 'B' },
      { tool: 'path', label: 'Path', shortcut: 'P' },
      { tool: 'tree', label: 'Tree', shortcut: 'T' },
      { tool: 'spawn', label: 'P.Spawn', shortcut: 'S' },
      { tool: 'erase', label: 'Del', shortcut: 'E' },
    ];

    tools.forEach((t, index) => {
      const y = index * 40;
      const btn = this.createToolButton(0, y, t.tool, t.label, t.shortcut);
      this.toolbarContainer.add(btn);
      this.toolButtons.set(t.tool, btn);
    });

    // Properties section – enough gap below tools (last tool ends ~325, so start at 358)
    const propsY = 358;
    const propsLabel = this.add.text(padding, propsY, 'PATHS', {
      fontSize: '16px',
      color: '#888888',
      fontStyle: 'bold',
    });
    this.editorContainer.add(propsLabel);

    this.propertiesContainer = this.add.container(padding, propsY + 26);
    this.editorContainer.add(this.propertiesContainer);

    // Biome selector – 2x2 grid; label and buttons with clear spacing
    const biomeLabel = this.add.text(0, -18, 'Biome:', {
      fontSize: '13px',
      color: '#888888',
    });
    this.propertiesContainer.add(biomeLabel);
    const btnW = 68;
    const btnH = 28;
    const gap = 10;
    const biomeGridTop = 4;
    this.biomeForestButton = this.createSmallButton(0, biomeGridTop, 'Forest', 0x228b22, () => {
      if (this.currentLevel) {
        this.currentLevel.biome = 'forest';
        this.isDirty = true;
        this.updateBiomeButtons();
      }
    }, btnW);
    this.propertiesContainer.add(this.biomeForestButton);
    this.biomeBogButton = this.createSmallButton(btnW + gap, biomeGridTop, 'Bog', 0x556b2f, () => {
      if (this.currentLevel) {
        this.currentLevel.biome = 'bog';
        this.isDirty = true;
        this.updateBiomeButtons();
      }
    }, btnW);
    this.propertiesContainer.add(this.biomeBogButton);
    this.biomeRuinsButton = this.createSmallButton(0, biomeGridTop + btnH + gap, 'Ruins', 0x6b5d4f, () => {
      if (this.currentLevel) {
        this.currentLevel.biome = 'ruins';
        this.isDirty = true;
        this.updateBiomeButtons();
      }
    }, btnW);
    this.propertiesContainer.add(this.biomeRuinsButton);
    this.biomeVolcanoButton = this.createSmallButton(btnW + gap, biomeGridTop + btnH + gap, 'Volcano', 0x8b3510, () => {
      if (this.currentLevel) {
        this.currentLevel.biome = 'volcano';
        this.isDirty = true;
        this.updateBiomeButtons();
      }
    }, btnW);
    this.propertiesContainer.add(this.biomeVolcanoButton);

    // Path section: + PATH button then path list, both below biome grid (no overlap)
    const biomeGridBottom = biomeGridTop + (btnH + gap) * 2;
    const pathBlockTop = biomeGridBottom + 14;
    this.pathListStartY = pathBlockTop + 32;  // reserved for "+ PATH" button; path rows start below
    const addPathBtn = this.createSmallButton(0, pathBlockTop, '+ PATH', 0x44aa44, () => {
      this.addNewPath();
    }, 80);
    this.propertiesContainer.add(addPathBtn);

    // Resources section
    const resourcesLabel = this.add.text(padding, 570, 'RESOURCES', {
      fontSize: '16px',
      color: '#888888',
      fontStyle: 'bold',
    });
    this.editorContainer.add(resourcesLabel);

    this.createResourcesUI();
  }

  private resourcesContainer!: Phaser.GameObjects.Container;
  private coinsValueText!: Phaser.GameObjects.Text;
  private gemsValueText!: Phaser.GameObjects.Text;

  private createResourcesUI(): void {
    const padding = 18;
    this.resourcesContainer = this.add.container(padding, 595);
    this.editorContainer.add(this.resourcesContainer);

    // Coins row
    const coinsLabel = this.add.text(0, 0, 'Coins:', {
      fontSize: '13px',
      color: '#ffdd44',
    });
    this.resourcesContainer.add(coinsLabel);

    const coinsInput = this.createResourceInput(55, -2, () => this.currentLevel?.startingCoins || 100, 
      (value) => {
        if (this.currentLevel) {
          this.currentLevel.startingCoins = value;
          this.isDirty = true;
        }
      }, 50, 0, 9999);
    this.resourcesContainer.add(coinsInput);

    // Gems row
    const gemsLabel = this.add.text(0, 30, 'Gems:', {
      fontSize: '13px',
      color: '#44ddff',
    });
    this.resourcesContainer.add(gemsLabel);

    const gemsInput = this.createResourceInput(55, 28, () => this.currentLevel?.startingGems || 25,
      (value) => {
        if (this.currentLevel) {
          this.currentLevel.startingGems = value;
          this.isDirty = true;
        }
      }, 10, 0, 999);
    this.resourcesContainer.add(gemsInput);
  }

  private createResourceInput(
    x: number,
    y: number,
    getValue: () => number,
    onChange: (value: number) => void,
    step: number,
    min: number,
    max: number
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const valueWidth = 52;
    const btnSize = 22;
    const gap = 4;
    const totalWidth = btnSize + gap + valueWidth + gap + btnSize;

    // Value text (created first so callbacks can reference it)
    const valueText = this.add.text(btnSize + gap + valueWidth / 2, 11, getValue().toString(), {
      fontSize: '13px',
      color: '#ffffff',
    });
    valueText.setOrigin(0.5);

    // Decrease button (left)
    const decBg = this.add.rectangle(0, 11, btnSize, btnSize, 0x5a4a4a);
    decBg.setStrokeStyle(1, 0x8a6a6a);
    decBg.setInteractive({ useHandCursor: true });
    container.add(decBg);
    const decreaseBtn = this.add.text(0, 11, '−', {
      fontSize: '18px',
      color: '#ff8888',
      fontStyle: 'bold',
    });
    decreaseBtn.setOrigin(0.5);
    decreaseBtn.setInteractive({ useHandCursor: true });
    decreaseBtn.on('pointerdown', () => {
      const currentValue = getValue();
      const newValue = Math.max(min, currentValue - step);
      valueText.setText(newValue.toString());
      onChange(newValue);
    });
    decBg.on('pointerdown', () => decreaseBtn.emit('pointerdown', {} as Phaser.Input.Pointer));
    container.add(decreaseBtn);

    // Value background and text
    const valueBg = this.add.rectangle(btnSize + gap + valueWidth / 2, 11, valueWidth, btnSize, 0x4a4a6e);
    valueBg.setStrokeStyle(1, 0x6a6a8e);
    container.add(valueBg);
    container.add(valueText);

    if (y < 15) {
      this.coinsValueText = valueText;
    } else {
      this.gemsValueText = valueText;
    }

    // Increase button (right)
    const incBg = this.add.rectangle(totalWidth - btnSize / 2, 11, btnSize, btnSize, 0x4a5a4a);
    incBg.setStrokeStyle(1, 0x6a8a6a);
    incBg.setInteractive({ useHandCursor: true });
    container.add(incBg);
    const increaseBtn = this.add.text(totalWidth - btnSize / 2, 11, '+', {
      fontSize: '18px',
      color: '#88ff88',
      fontStyle: 'bold',
    });
    increaseBtn.setOrigin(0.5);
    increaseBtn.setInteractive({ useHandCursor: true });
    increaseBtn.on('pointerdown', () => {
      const currentValue = getValue();
      const newValue = Math.min(max, currentValue + step);
      valueText.setText(newValue.toString());
      onChange(newValue);
    });
    incBg.on('pointerdown', () => increaseBtn.emit('pointerdown', {} as Phaser.Input.Pointer));
    container.add(increaseBtn);

    return container;
  }

  private createMapArea(): void {
    this.mapContainer = this.add.container(MAP_OFFSET_X, MAP_OFFSET_Y);
    this.editorContainer.add(this.mapContainer);

    // Grid graphics
    this.gridGraphics = this.add.graphics();
    this.mapContainer.add(this.gridGraphics);

    // Path graphics (drawn below objects)
    this.pathGraphics = this.add.graphics();
    this.mapContainer.add(this.pathGraphics);

    // Cursor highlight
    this.cursorGraphics = this.add.graphics();
    this.mapContainer.add(this.cursorGraphics);
  }

  private createToolButton(x: number, y: number, tool: EditorTool, label: string, shortcut: string): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    const bg = this.add.rectangle(65, 13, 130, 30, 0x3a3a5e);
    bg.setStrokeStyle(2, TOOL_COLORS[tool]);
    bg.setInteractive({ useHandCursor: true });
    container.add(bg);

    const text = this.add.text(10, 5, `[${shortcut}] ${label}`, {
      fontSize: '13px',
      color: '#ffffff',
    });
    container.add(text);

    bg.on('pointerdown', () => {
      this.selectTool(tool);
    });

    return container;
  }

  // ============================================
  // Map Rendering
  // ============================================

  private drawGrid(): void {
    this.gridGraphics.clear();

    const width = this.mapWidth * GRID_TILE_SIZE;
    const height = this.mapHeight * GRID_TILE_SIZE;

    // Background
    this.gridGraphics.fillStyle(0x1a2a1a, 1);
    this.gridGraphics.fillRect(0, 0, width, height);

    // Highlight edge tiles (spawn point areas) with subtle color
    this.gridGraphics.fillStyle(0x331111, 1);
    // Top edge
    this.gridGraphics.fillRect(0, 0, width, GRID_TILE_SIZE);
    // Bottom edge
    this.gridGraphics.fillRect(0, height - GRID_TILE_SIZE, width, GRID_TILE_SIZE);
    // Left edge
    this.gridGraphics.fillRect(0, 0, GRID_TILE_SIZE, height);
    // Right edge
    this.gridGraphics.fillRect(width - GRID_TILE_SIZE, 0, GRID_TILE_SIZE, height);

    // Grid lines
    this.gridGraphics.lineStyle(1, 0x333333, 0.5);

    for (let x = 0; x <= this.mapWidth; x++) {
      this.gridGraphics.lineBetween(x * GRID_TILE_SIZE, 0, x * GRID_TILE_SIZE, height);
    }

    for (let y = 0; y <= this.mapHeight; y++) {
      this.gridGraphics.lineBetween(0, y * GRID_TILE_SIZE, width, y * GRID_TILE_SIZE);
    }

    // Border
    this.gridGraphics.lineStyle(2, 0x555555, 1);
    this.gridGraphics.strokeRect(0, 0, width, height);
  }

  private drawMapObjects(): void {
    if (!this.currentLevel) return;

    // Clear and redraw path graphics
    this.pathGraphics.clear();

    // Draw paths
    this.currentLevel.paths.forEach((path, pathIndex) => {
      const color = this.getPathColor(pathIndex);
      
      if (path.points && path.points.length > 1) {
        this.drawPathLine(path.points, color);
      }
      
      if (path.routes) {
        Object.values(path.routes).forEach((route, routeIndex) => {
          const routeColor = Phaser.Display.Color.ValueToColor(color).lighten(routeIndex * 20).color;
          this.drawPathLine(route, routeColor);
        });
      }
    });

    // Draw in-progress path
    if (this.isDrawingPath && this.pathPoints.length > 0) {
      this.drawPathLine(this.pathPoints, 0xffff00, true);
    }

    // Collect and draw monster spawn points (first point of each path = edge spawn)
    this.drawMonsterSpawnPoints();

    // Draw base (only if placed - valid position)
    const base = this.currentLevel.base;
    if (base.x >= 0 && base.y >= 0) {
      this.pathGraphics.fillStyle(0x44ff44, 1);
      this.pathGraphics.fillCircle(
        base.x * GRID_TILE_SIZE + GRID_TILE_SIZE / 2,
        base.y * GRID_TILE_SIZE + GRID_TILE_SIZE / 2,
        GRID_TILE_SIZE * 0.4
      );
      this.pathGraphics.lineStyle(2, 0x22aa22);
      this.pathGraphics.strokeCircle(
        base.x * GRID_TILE_SIZE + GRID_TILE_SIZE / 2,
        base.y * GRID_TILE_SIZE + GRID_TILE_SIZE / 2,
        GRID_TILE_SIZE * 0.4
      );
    }

    // Draw player spawns
    this.currentLevel.playerSpawns.forEach((spawn, index) => {
      const colors = [0x4488ff, 0xff4444, 0xffff44, 0x44ff44];
      this.pathGraphics.fillStyle(colors[index % colors.length], 1);
      this.pathGraphics.fillRect(
        spawn.x * GRID_TILE_SIZE + 5,
        spawn.y * GRID_TILE_SIZE + 5,
        GRID_TILE_SIZE - 10,
        GRID_TILE_SIZE - 10
      );
    });

    // Draw trees
    this.currentLevel.treeSlots.forEach(tree => {
      this.pathGraphics.fillStyle(0x228822, 1);
      this.pathGraphics.fillCircle(
        tree.x * GRID_TILE_SIZE + GRID_TILE_SIZE / 2,
        tree.y * GRID_TILE_SIZE + GRID_TILE_SIZE / 2,
        GRID_TILE_SIZE * 0.3
      );
    });
  }

  /**
   * Draw monster spawn points (where paths start at map edge)
   * Shows count when multiple paths share the same spawn
   */
  private drawMonsterSpawnPoints(): void {
    if (!this.currentLevel) return;

    // Collect spawn points and count how many paths use each
    const spawnCounts: Map<string, { x: number; y: number; count: number; pathIds: string[] }> = new Map();

    this.currentLevel.paths.forEach((path, index) => {
      if (path.points && path.points.length > 0) {
        // First point is the spawn (monsters walk from spawn to base)
        const spawn = path.points[0];
        const key = `${spawn.x},${spawn.y}`;
        
        if (spawnCounts.has(key)) {
          const entry = spawnCounts.get(key)!;
          entry.count++;
          entry.pathIds.push(path.id);
        } else {
          spawnCounts.set(key, { 
            x: spawn.x, 
            y: spawn.y, 
            count: 1, 
            pathIds: [path.id] 
          });
        }
      }
    });

    // Draw spawn point markers
    spawnCounts.forEach((spawn, key) => {
      const centerX = spawn.x * GRID_TILE_SIZE + GRID_TILE_SIZE / 2;
      const centerY = spawn.y * GRID_TILE_SIZE + GRID_TILE_SIZE / 2;

      // Draw spawn marker (red diamond shape)
      this.pathGraphics.fillStyle(0xff0000, 0.8);
      this.pathGraphics.beginPath();
      this.pathGraphics.moveTo(centerX, centerY - GRID_TILE_SIZE * 0.4);
      this.pathGraphics.lineTo(centerX + GRID_TILE_SIZE * 0.4, centerY);
      this.pathGraphics.lineTo(centerX, centerY + GRID_TILE_SIZE * 0.4);
      this.pathGraphics.lineTo(centerX - GRID_TILE_SIZE * 0.4, centerY);
      this.pathGraphics.closePath();
      this.pathGraphics.fillPath();

      // White border
      this.pathGraphics.lineStyle(2, 0xffffff, 1);
      this.pathGraphics.beginPath();
      this.pathGraphics.moveTo(centerX, centerY - GRID_TILE_SIZE * 0.4);
      this.pathGraphics.lineTo(centerX + GRID_TILE_SIZE * 0.4, centerY);
      this.pathGraphics.lineTo(centerX, centerY + GRID_TILE_SIZE * 0.4);
      this.pathGraphics.lineTo(centerX - GRID_TILE_SIZE * 0.4, centerY);
      this.pathGraphics.closePath();
      this.pathGraphics.strokePath();

      // Show count if multiple paths share this spawn
      if (spawn.count > 1) {
        // Background circle for number
        this.pathGraphics.fillStyle(0xffff00, 1);
        this.pathGraphics.fillCircle(centerX + GRID_TILE_SIZE * 0.35, centerY - GRID_TILE_SIZE * 0.35, 10);
        this.pathGraphics.lineStyle(1, 0x000000, 1);
        this.pathGraphics.strokeCircle(centerX + GRID_TILE_SIZE * 0.35, centerY - GRID_TILE_SIZE * 0.35, 10);
      }
    });

    // Draw count numbers as text (need to use scene's add since graphics can't do text)
    // We'll store these in a temporary array and clean up
    this.cleanupSpawnCountTexts();
    
    spawnCounts.forEach((spawn) => {
      if (spawn.count > 1) {
        const centerX = spawn.x * GRID_TILE_SIZE + GRID_TILE_SIZE / 2;
        const centerY = spawn.y * GRID_TILE_SIZE + GRID_TILE_SIZE / 2;
        
        const countText = this.add.text(
          centerX + GRID_TILE_SIZE * 0.35,
          centerY - GRID_TILE_SIZE * 0.35,
          spawn.count.toString(),
          { fontSize: '12px', color: '#000000', fontStyle: 'bold' }
        );
        countText.setOrigin(0.5);
        this.mapContainer.add(countText);
        this.spawnCountTexts.push(countText);
      }
    });
  }

  private cleanupSpawnCountTexts(): void {
    this.spawnCountTexts.forEach(t => t.destroy());
    this.spawnCountTexts = [];
  }

  private drawPathLine(points: TilePosition[], color: number, dashed: boolean = false): void {
    if (points.length < 2) return;

    // Convert to world coordinates
    const worldPoints = points.map(p => ({
      x: p.x * GRID_TILE_SIZE + GRID_TILE_SIZE / 2,
      y: p.y * GRID_TILE_SIZE + GRID_TILE_SIZE / 2,
    }));

    // Draw light grey straight lines between waypoints first (underneath)
    this.pathGraphics.lineStyle(2, 0x555555, 0.5);
    this.pathGraphics.beginPath();
    this.pathGraphics.moveTo(worldPoints[0].x, worldPoints[0].y);
    for (let i = 1; i < worldPoints.length; i++) {
      this.pathGraphics.lineTo(worldPoints[i].x, worldPoints[i].y);
    }
    this.pathGraphics.strokePath();

    // Draw curved path (Catmull-Rom spline) in main color
    const curvedPoints = this.getCatmullRomCurve(worldPoints, 10);
    
    this.pathGraphics.lineStyle(4, color, dashed ? 0.6 : 1);
    this.pathGraphics.beginPath();
    this.pathGraphics.moveTo(curvedPoints[0].x, curvedPoints[0].y);
    for (let i = 1; i < curvedPoints.length; i++) {
      this.pathGraphics.lineTo(curvedPoints[i].x, curvedPoints[i].y);
    }
    this.pathGraphics.strokePath();

    // Draw waypoint markers
    points.forEach((point, index) => {
      const isFirst = index === 0;
      const isLast = index === points.length - 1;
      const wx = point.x * GRID_TILE_SIZE + GRID_TILE_SIZE / 2;
      const wy = point.y * GRID_TILE_SIZE + GRID_TILE_SIZE / 2;
      
      // Waypoint circle
      this.pathGraphics.fillStyle(isFirst ? 0xff0000 : (isLast ? 0x00ff00 : color), 1);
      this.pathGraphics.fillCircle(wx, wy, 6);
      
      // White border for visibility
      this.pathGraphics.lineStyle(1, 0xffffff, 0.8);
      this.pathGraphics.strokeCircle(wx, wy, 6);
    });
  }

  /**
   * Generate Catmull-Rom spline curve points (same algorithm the game uses)
   */
  private getCatmullRomCurve(
    points: Array<{ x: number; y: number }>,
    resolution: number = 10
  ): Array<{ x: number; y: number }> {
    if (points.length < 2) return points;
    if (points.length === 2) return points;

    const result: Array<{ x: number; y: number }> = [];

    // Add phantom points at start and end for proper curve endpoints
    const extended = [
      { x: points[0].x * 2 - points[1].x, y: points[0].y * 2 - points[1].y },
      ...points,
      { 
        x: points[points.length - 1].x * 2 - points[points.length - 2].x, 
        y: points[points.length - 1].y * 2 - points[points.length - 2].y 
      },
    ];

    // Generate curve segments
    for (let i = 1; i < extended.length - 2; i++) {
      const p0 = extended[i - 1];
      const p1 = extended[i];
      const p2 = extended[i + 1];
      const p3 = extended[i + 2];

      for (let t = 0; t < resolution; t++) {
        const s = t / resolution;
        const s2 = s * s;
        const s3 = s2 * s;

        // Catmull-Rom basis functions
        const x =
          0.5 * (
            2 * p1.x +
            (-p0.x + p2.x) * s +
            (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * s2 +
            (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * s3
          );
        const y =
          0.5 * (
            2 * p1.y +
            (-p0.y + p2.y) * s +
            (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * s2 +
            (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * s3
          );

        result.push({ x, y });
      }
    }

    // Add final point
    result.push(points[points.length - 1]);

    return result;
  }

  private getPathColor(index: number): number {
    const colors = [0xffaa00, 0x00aaff, 0xff00aa, 0xaaff00, 0x00ffaa, 0xaa00ff];
    return colors[index % colors.length];
  }

  private updateCursor(): void {
    this.cursorGraphics.clear();

    if (!this.hoveredTile) return;

    let color = TOOL_COLORS[this.currentTool];
    
    // Highlight edge tiles specially when drawing path (these are spawn points)
    const isEdge = this.isEdgeTile(this.hoveredTile);
    if (this.currentTool === 'path' && this.isDrawingPath && isEdge) {
      color = 0xff0000;  // Red for spawn point (edge)
    }

    this.cursorGraphics.lineStyle(2, color, 0.8);
    this.cursorGraphics.strokeRect(
      this.hoveredTile.x * GRID_TILE_SIZE,
      this.hoveredTile.y * GRID_TILE_SIZE,
      GRID_TILE_SIZE,
      GRID_TILE_SIZE
    );

    // Show ghost based on tool
    this.cursorGraphics.fillStyle(color, 0.3);
    this.cursorGraphics.fillRect(
      this.hoveredTile.x * GRID_TILE_SIZE,
      this.hoveredTile.y * GRID_TILE_SIZE,
      GRID_TILE_SIZE,
      GRID_TILE_SIZE
    );

    // Draw line from last path point to cursor when drawing
    if (this.currentTool === 'path' && this.isDrawingPath && this.pathPoints.length > 0) {
      const lastPoint = this.pathPoints[this.pathPoints.length - 1];
      this.cursorGraphics.lineStyle(2, 0xffff00, 0.6);
      this.cursorGraphics.lineBetween(
        lastPoint.x * GRID_TILE_SIZE + GRID_TILE_SIZE / 2,
        lastPoint.y * GRID_TILE_SIZE + GRID_TILE_SIZE / 2,
        this.hoveredTile.x * GRID_TILE_SIZE + GRID_TILE_SIZE / 2,
        this.hoveredTile.y * GRID_TILE_SIZE + GRID_TILE_SIZE / 2
      );
    }
  }

  private refreshPathList(): void {
    // Clear existing path items
    this.pathListItems.forEach(item => item.destroy());
    this.pathListItems = [];

    if (!this.currentLevel) return;

    const pathRowWidth = 170;
    const startY = this.pathListStartY;
    this.currentLevel.paths.forEach((path, index) => {
      const y = startY + index * 26;
      const container = this.add.container(0, y);

      const color = this.getPathColor(index);
      const bg = this.add.rectangle(pathRowWidth / 2, 0, pathRowWidth, 22, 
        this.currentPathId === path.id ? 0x4a4a6e : 0x3a3a5e);
      bg.setStrokeStyle(1, color);
      bg.setInteractive({ useHandCursor: true });
      container.add(bg);

      const text = this.add.text(8, -7, `${path.id.replace('path-', 'P')} (${path.points?.length || 0})`, {
        fontSize: '11px',
        color: '#ffffff',
      });
      container.add(text);

      // Delete button
      const delBtn = this.add.text(pathRowWidth - 12, -7, 'X', {
        fontSize: '11px',
        color: '#ff4444',
      });
      delBtn.setInteractive({ useHandCursor: true });
      delBtn.on('pointerdown', (e: Phaser.Input.Pointer) => {
        e.event?.stopPropagation();
        this.deletePath(path.id);
      });
      container.add(delBtn);

      bg.on('pointerdown', () => {
        this.selectPath(path.id);
      });

      this.propertiesContainer.add(container);
      this.pathListItems.push(container);
    });
  }

  // ============================================
  // Input Handling
  // ============================================

  private setupInput(): void {
    // Mouse move
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.mode !== 'editor') return;

      const localX = pointer.x - MAP_OFFSET_X;
      const localY = pointer.y - MAP_OFFSET_Y;

      if (localX >= 0 && localY >= 0 && 
          localX < this.mapWidth * GRID_TILE_SIZE && 
          localY < this.mapHeight * GRID_TILE_SIZE) {
        this.hoveredTile = {
          x: Math.floor(localX / GRID_TILE_SIZE),
          y: Math.floor(localY / GRID_TILE_SIZE),
        };
        this.updateCursor();
        this.updateStatus();
      } else {
        this.hoveredTile = null;
        this.cursorGraphics.clear();
      }
    });

    // Mouse click
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.mode !== 'editor' || !this.hoveredTile) return;

      // Check if clicking on map area
      const localX = pointer.x - MAP_OFFSET_X;
      const localY = pointer.y - MAP_OFFSET_Y;

      if (localX < 0 || localY < 0 || 
          localX >= this.mapWidth * GRID_TILE_SIZE || 
          localY >= this.mapHeight * GRID_TILE_SIZE) {
        return;
      }

      this.handleMapClick(this.hoveredTile, pointer);
    });

    // Keyboard shortcuts
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.mode !== 'editor') return;

      switch (event.key.toUpperCase()) {
        case 'V': this.selectTool('select'); break;
        case 'B': this.selectTool('base'); break;
        case 'P': this.selectTool('path'); break;
        case 'T': this.selectTool('tree'); break;
        case 'S': this.selectTool('spawn'); break;
        case 'E': this.selectTool('erase'); break;
        case 'ESCAPE': 
          if (this.isDrawingPath) {
            this.cancelPathDrawing();
          }
          break;
        case 'Z':
          if (event.ctrlKey || event.metaKey) {
            // TODO: Undo
          }
          break;
      }
    });
  }

  private handleMapClick(tile: TilePosition, pointer: Phaser.Input.Pointer): void {
    if (!this.currentLevel) return;

    switch (this.currentTool) {
      case 'base':
        this.currentLevel.base = { ...tile };
        this.isDirty = true;
        break;

      case 'spawn':
        // Add or move player spawn
        if (this.currentLevel.playerSpawns.length < 4) {
          // Check if spawn already exists at this location
          const existingIndex = this.currentLevel.playerSpawns.findIndex(
            s => s.x === tile.x && s.y === tile.y
          );
          if (existingIndex === -1) {
            this.currentLevel.playerSpawns.push({ ...tile });
            this.isDirty = true;
          }
        }
        break;

      case 'tree': {
        // Add tree if not already there
        const existingTree = this.currentLevel.treeSlots.find(
          t => t.x === tile.x && t.y === tile.y
        );
        if (!existingTree) {
          this.currentLevel.treeSlots.push({
            x: tile.x,
            y: tile.y,
            id: `tree-${this.currentLevel.treeSlots.length + 1}`,
          });
          this.isDirty = true;
        }
        break;
      }

      case 'path':
        this.handlePathClick(tile);
        break;

      case 'erase':
        this.eraseAt(tile);
        break;
    }

    this.drawMapObjects();
  }

  private eraseAt(tile: TilePosition): void {
    if (!this.currentLevel) return;

    // Erase tree
    const treeIndex = this.currentLevel.treeSlots.findIndex(
      t => t.x === tile.x && t.y === tile.y
    );
    if (treeIndex !== -1) {
      this.currentLevel.treeSlots.splice(treeIndex, 1);
      this.isDirty = true;
    }

    // Erase spawn
    const spawnIndex = this.currentLevel.playerSpawns.findIndex(
      s => s.x === tile.x && s.y === tile.y
    );
    if (spawnIndex !== -1 && this.currentLevel.playerSpawns.length > 1) {
      this.currentLevel.playerSpawns.splice(spawnIndex, 1);
      this.isDirty = true;
    }

    // Erase path point (more complex - would need to handle breaking paths)
    // For now, just remove entire path if clicking on its point
    this.currentLevel.paths.forEach((path, pathIndex) => {
      if (path.points) {
        const pointIndex = path.points.findIndex(
          p => p.x === tile.x && p.y === tile.y
        );
        if (pointIndex !== -1) {
          this.currentLevel!.paths.splice(pathIndex, 1);
          this.isDirty = true;
          this.refreshPathList();
        }
      }
    });
  }

  /**
   * Check if a tile is on the edge of the map (spawn point location)
   */
  private isEdgeTile(tile: TilePosition): boolean {
    return tile.x === 0 || tile.x === this.mapWidth - 1 || 
           tile.y === 0 || tile.y === this.mapHeight - 1;
  }

  /**
   * Handle path tool clicks:
   * - First click starts path (from base if placed, otherwise from click location)
   * - Each click adds a waypoint
   * - Click on edge finishes path (that's the spawn point)
   */
  private handlePathClick(tile: TilePosition): void {
    if (!this.currentLevel) return;

    const baseIsPlaced = this.currentLevel.base.x >= 0 && this.currentLevel.base.y >= 0;

    if (!this.isDrawingPath) {
      // Start new path
      this.isDrawingPath = true;
      
      if (baseIsPlaced) {
        // Start from base location
        this.pathPoints = [{ ...this.currentLevel.base }];
        
        // If first click is not on base, add it as second point
        if (tile.x !== this.currentLevel.base.x || tile.y !== this.currentLevel.base.y) {
          this.pathPoints.push({ ...tile });
        }
      } else {
        // No base yet - start from clicked location
        this.pathPoints = [{ ...tile }];
      }
      
      // Check if starting click is on edge - immediately finish (need at least 2 points)
      if (this.isEdgeTile(tile) && this.pathPoints.length >= 2) {
        this.finishPathDrawing();
        return;
      }
    } else {
      // Add waypoint
      const lastPoint = this.pathPoints[this.pathPoints.length - 1];
      if (lastPoint.x !== tile.x || lastPoint.y !== tile.y) {
        this.pathPoints.push({ ...tile });
      }
      
      // If clicked on edge, finish the path (edge is spawn point)
      if (this.isEdgeTile(tile)) {
        this.finishPathDrawing();
        return;
      }
    }

    this.drawMapObjects();
    this.updateStatus();
  }

  private finishPathDrawing(): void {
    if (!this.currentLevel || this.pathPoints.length < 2) {
      this.cancelPathDrawing();
      return;
    }

    // Path goes from spawn (edge) to base, so reverse the points
    // We drew from base to edge, but monsters walk edge to base
    const reversedPoints = [...this.pathPoints].reverse();

    // Create new path
    const pathId = `path-${this.pathIdCounter++}`;
    const newPath: PathDef = {
      id: pathId,
      points: reversedPoints,
      curved: true,
      curveResolution: 8,
    };

    this.currentLevel.paths.push(newPath);
    this.isDirty = true;

    // Reset drawing state
    this.isDrawingPath = false;
    this.pathPoints = [];
    this.currentPathId = pathId;

    console.log(`[MapEditor] Created path ${pathId} with ${reversedPoints.length} points (spawn at edge to base)`);

    this.refreshPathList();
    this.drawMapObjects();
    
    // Refresh wave builder to show new path in dropdowns
    if (this.waveBuilderVisible) {
      this.refreshWaveList();
    }
  }

  private cancelPathDrawing(): void {
    this.isDrawingPath = false;
    this.pathPoints = [];
    this.drawMapObjects();
  }

  // ============================================
  // Tool Management
  // ============================================

  private selectTool(tool: EditorTool): void {
    // Cancel path drawing if switching tools
    if (this.isDrawingPath && tool !== 'path') {
      this.cancelPathDrawing();
    }

    this.currentTool = tool;

    // Update button highlights
    this.toolButtons.forEach((btn, t) => {
      const bg = btn.first as Phaser.GameObjects.Rectangle;
      if (bg) {
        bg.setFillStyle(t === tool ? 0x5a5a7e : 0x3a3a5e);
      }
    });

    this.updateStatus();
  }

  private selectPath(pathId: string): void {
    this.currentPathId = pathId;
    this.refreshPathList();
  }

  private addNewPath(): void {
    // Select path tool and prepare for drawing
    this.selectTool('path');
    this.updateStatus();
  }

  private deletePath(pathId: string): void {
    if (!this.currentLevel) return;

    const index = this.currentLevel.paths.findIndex(p => p.id === pathId);
    if (index !== -1) {
      this.currentLevel.paths.splice(index, 1);
      this.isDirty = true;
      this.refreshPathList();
      this.drawMapObjects();
      
      // Update wave spawns that reference this path
      this.validateWavePaths();
      if (this.waveBuilderVisible) {
        this.refreshWaveList();
      }
    }
  }

  // ============================================
  // Level Management
  // ============================================

  private promptNewLevel(): void {
    // Simple prompt using browser dialog
    const name = prompt('Enter map name:');
    if (name && name.trim()) {
      const level = CustomLevelManager.createNewLevel(name.trim());
      this.loadLevel(level);
    }
  }

  private loadLevel(level: CustomLevel): void {
    this.currentLevel = level;
    this.mode = 'editor';
    this.isDirty = false;

    // Update map dimensions
    this.mapWidth = level.mapSize.w;
    this.mapHeight = level.mapSize.h;

    // Find highest path ID
    this.pathIdCounter = 1;
    level.paths.forEach(p => {
      const match = p.id.match(/path-(\d+)/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num >= this.pathIdCounter) {
          this.pathIdCounter = num + 1;
        }
      }
    });

    // Switch to editor mode
    this.menuContainer.setVisible(false);
    this.editorContainer.setVisible(true);

    // Update UI
    this.levelNameText.setText(level.name);
    this.drawGrid();
    this.drawMapObjects();
    this.refreshPathList();
    this.selectTool('select');
    this.updateStatus();

    // Update resource display
    this.refreshResourceDisplay();

    // Reset wave builder state
    this.selectedWaveIndex = -1;
    this.scrollOffset = 0;
    if (this.waveBuilderVisible) {
      this.refreshWaveList();
    }
    
    // Refresh tower panel if visible
    if (this.towerPanelVisible) {
      this.refreshTowerList();
    }
  }

  private refreshResourceDisplay(): void {
    if (!this.currentLevel) return;
    
    if (this.coinsValueText) {
      this.coinsValueText.setText(this.currentLevel.startingCoins.toString());
    }
    if (this.gemsValueText) {
      this.gemsValueText.setText((this.currentLevel.startingGems || 25).toString());
    }
    this.updateBiomeButtons();
  }

  private updateBiomeButtons(): void {
    const biome = this.currentLevel?.biome ?? 'forest';
    const forestBg = this.biomeForestButton?.list?.[0] as Phaser.GameObjects.Rectangle | undefined;
    const bogBg = this.biomeBogButton?.list?.[0] as Phaser.GameObjects.Rectangle | undefined;
    const ruinsBg = this.biomeRuinsButton?.list?.[0] as Phaser.GameObjects.Rectangle | undefined;
    const volcanoBg = this.biomeVolcanoButton?.list?.[0] as Phaser.GameObjects.Rectangle | undefined;
    if (forestBg) forestBg.setAlpha(biome === 'forest' ? 1 : 0.5);
    if (bogBg) bogBg.setAlpha(biome === 'bog' ? 1 : 0.5);
    if (ruinsBg) ruinsBg.setAlpha(biome === 'ruins' ? 1 : 0.5);
    if (volcanoBg) volcanoBg.setAlpha(biome === 'volcano' ? 1 : 0.5);
  }

  private saveCurrentLevel(): void {
    if (!this.currentLevel) return;

    // Validate level
    if (this.currentLevel.paths.length === 0) {
      alert('Please add at least one path before saving.');
      return;
    }

    // Only auto-generate waves if no custom waves have been defined
    // This preserves user-created waves from the wave builder
    if (this.currentLevel.waves.length === 0) {
      this.currentLevel.waves = CustomLevelManager.generateWaves(this.currentLevel);
    }

    // Update pathIds in waves if paths have changed
    this.validateWavePaths();

    CustomLevelManager.saveLevel(this.currentLevel);
    this.isDirty = false;
    this.updateStatus();

    // Show feedback
    const saved = this.add.text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, 'SAVED!', {
      fontSize: '48px',
      color: '#44ff44',
      fontStyle: 'bold',
    });
    saved.setOrigin(0.5);
    saved.setDepth(1000);

    this.tweens.add({
      targets: saved,
      alpha: 0,
      y: DESIGN_HEIGHT / 2 - 50,
      duration: 1000,
      onComplete: () => saved.destroy(),
    });
  }

  /**
   * Export current map as JSON in the same format as game level definitions
   * (paths, waves, boss waves, timings, resources, etc.). Copies to clipboard and triggers download.
   */
  private exportCurrentMap(): void {
    if (!this.currentLevel) return;

    const payload = this.buildExportPayload();
    const json = JSON.stringify(payload, null, 2);

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(json);
      }
    } catch (_) {
      // Clipboard may fail in some contexts
    }

    try {
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(this.currentLevel.name || 'map').replace(/[^a-zA-Z0-9-_]/g, '_')}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.warn('[MapEditor] Export download failed:', e);
    }

    const msg = this.add.text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, 'Exported!\n(Copied to clipboard + file download)', {
      fontSize: '20px',
      color: '#dddd88',
      align: 'center',
    });
    msg.setOrigin(0.5);
    msg.setDepth(1000);
    this.tweens.add({
      targets: msg,
      alpha: 0,
      duration: 1500,
      onComplete: () => msg.destroy(),
    });
  }

  /**
   * Build a ScenarioDef-like object for export (same shape as level definitions in LevelLoader).
   */
  private buildExportPayload(): Record<string, unknown> {
    if (!this.currentLevel) return {};

    const L = this.currentLevel;
    return {
      id: L.id,
      name: L.name,
      description: L.description,
      difficulty: L.difficulty,
      region: L.region,
      regionNumber: L.regionNumber,
      mapSize: L.mapSize,
      tileSize: L.tileSize,
      base: L.base,
      playerSpawns: L.playerSpawns,
      treeSlots: L.treeSlots,
      paths: L.paths,
      waves: L.waves,
      startingCoins: L.startingCoins,
      startingGems: L.startingGems,
      startingVillagers: L.startingVillagers,
      allowedTowers: L.allowedTowers,
      unlocks: L.unlocks ?? [],
      hasManualTrees: L.hasManualTrees,
      biome: L.biome,
      preResearchedTowers: L.preResearchedTowers,
    };
  }

  /**
   * Ensure all wave spawns reference valid paths
   */
  private validateWavePaths(): void {
    if (!this.currentLevel) return;

    const validPathIds = new Set(this.currentLevel.paths.map(p => p.id));
    const defaultPathId = this.currentLevel.paths.length > 0 ? this.currentLevel.paths[0].id : 'path-1';

    this.currentLevel.waves.forEach(wave => {
      wave.spawns.forEach(spawn => {
        if (!validPathIds.has(spawn.pathId)) {
          spawn.pathId = defaultPathId;
        }
      });
    });
  }

  private testLevel(): void {
    if (!this.currentLevel) return;

    // Validate level has minimum required elements
    const errors: string[] = [];
    
    if (this.currentLevel.base.x < 0 || this.currentLevel.base.y < 0) {
      errors.push('- No BASE placed (use [B] tool)');
    }
    
    if (this.currentLevel.paths.length === 0) {
      errors.push('- No PATHS defined (use [P] tool)');
    }

    // If testing with waves, check that waves are defined
    if (this.testWithWaves && this.currentLevel.waves.length === 0) {
      errors.push('- No WAVES defined (open WAVES panel to add)');
    }

    if (errors.length > 0) {
      alert('Cannot test level:\n\n' + errors.join('\n') + '\n\nPlease add the missing elements.');
      return;
    }

    // Warn about optional missing elements
    const warnings: string[] = [];
    if (this.currentLevel.playerSpawns.length === 0) {
      warnings.push('- No player spawn point (players will spawn at base)');
    }
    if (this.currentLevel.treeSlots.length === 0) {
      warnings.push('- No trees placed');
    }

    if (warnings.length > 0) {
      const proceed = confirm('Level is missing optional elements:\n\n' + warnings.join('\n') + '\n\nContinue anyway?');
      if (!proceed) return;
    }

    // Save first
    if (this.isDirty) {
      this.saveCurrentLevel();
    }

    // Pass level to loader so unsaved/imported levels load (IMPORT_xxx not in storage)
    CustomLevelManager.setPendingLevelForTest(this.currentLevel);

    // Start game - either with custom waves or in experiment mode
    if (this.testWithWaves) {
      // Test with custom waves (normal gameplay)
      this.scene.start('GameplayScene', { scenarioId: this.currentLevel.id });
    } else {
      // Test in experiment mode (unlimited resources, manual spawning)
      GameState.startExperimentMode();
      this.scene.start('GameplayScene', { scenarioId: this.currentLevel.id });
    }
  }

  /**
   * Test a level directly from the menu without loading it into the editor
   */
  private testLevelDirectly(level: CustomLevel): void {
    // Validate level has minimum required elements
    const errors: string[] = [];
    
    if (level.base.x < 0 || level.base.y < 0) {
      errors.push('- No BASE placed');
    }
    
    if (level.paths.length === 0) {
      errors.push('- No PATHS defined');
    }

    if (errors.length > 0) {
      alert(`Cannot test "${level.name}":\n\n${errors.join('\n')}\n\nEdit the level first to add missing elements.`);
      return;
    }

    // Show test options popup
    this.showTestOptionsForLevel(level);
  }

  private showTestOptionsForLevel(level: CustomLevel): void {
    // Create a simple popup with test options
    const overlay = this.add.rectangle(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, DESIGN_WIDTH, DESIGN_HEIGHT, 0x000000, 0.5);
    overlay.setInteractive();
    overlay.setDepth(999);

    const popup = this.add.container(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2);
    popup.setDepth(1000);

    // Popup background
    const popupBg = this.add.rectangle(0, 0, 300, 200, 0x2a2a4e);
    popupBg.setStrokeStyle(2, 0x6a6a8e);
    popup.add(popupBg);

    // Title
    const title = this.add.text(0, -70, `TEST: ${level.name}`, {
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    popup.add(title);

    // Description
    const desc = this.add.text(0, -35, 'Choose how to test:', {
      fontSize: '14px',
      color: '#aaaaaa',
    });
    desc.setOrigin(0.5);
    popup.add(desc);

    // Test with Waves button
    const waveCount = level.waves.length;
    const wavesBtn = this.createButton(0, 10, `WAVES (${waveCount})`, waveCount > 0 ? 0x4488ff : 0x666666, () => {
      if (waveCount === 0) {
        alert('No waves defined. Edit the level and add waves first.');
        return;
      }
      this.cleanupTestPopup(overlay, popup);
      CustomLevelManager.setPendingLevelForTest(level);
      this.scene.start('GameplayScene', { scenarioId: level.id });
    });
    wavesBtn.setScale(1.2);
    popup.add(wavesBtn);

    // Test with Experiment mode button
    const expBtn = this.createButton(0, 60, 'EXPERIMENT', 0x44aa44, () => {
      this.cleanupTestPopup(overlay, popup);
      GameState.startExperimentMode();
      CustomLevelManager.setPendingLevelForTest(level);
      this.scene.start('GameplayScene', { scenarioId: level.id });
    });
    expBtn.setScale(1.2);
    popup.add(expBtn);

    // Cancel button
    const cancelBtn = this.add.text(120, -80, 'X', {
      fontSize: '20px',
      color: '#ff4444',
      fontStyle: 'bold',
    });
    cancelBtn.setOrigin(0.5);
    cancelBtn.setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerdown', () => {
      this.cleanupTestPopup(overlay, popup);
    });
    popup.add(cancelBtn);

    // Close on overlay click
    overlay.on('pointerdown', () => {
      this.cleanupTestPopup(overlay, popup);
    });
  }

  private exitEditor(): void {
    if (this.isDirty) {
      const save = confirm('You have unsaved changes. Save before exiting?');
      if (save) {
        this.saveCurrentLevel();
      }
    }

    // Return to menu
    this.mode = 'menu';
    this.currentLevel = null;
    this.menuContainer.setVisible(true);
    this.editorContainer.setVisible(false);
    this.refreshLevelList();
  }

  private confirmDeleteLevel(level: CustomLevel): void {
    if (confirm(`Delete "${level.name}"? This cannot be undone.`)) {
      CustomLevelManager.deleteLevel(level.id);
      this.refreshLevelList();
    }
  }

  // ============================================
  // UI Helpers
  // ============================================

  private createButton(x: number, y: number, text: string, color: number, onClick: () => void, width?: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const w = width ?? Math.max(100, text.length * 9);
    const bg = this.add.rectangle(0, 0, w, 40, color);
    bg.setStrokeStyle(2, Phaser.Display.Color.ValueToColor(color).lighten(30).color);
    bg.setInteractive({ useHandCursor: true });
    container.add(bg);

    const label = this.add.text(0, 0, text, {
      fontSize: '16px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    label.setOrigin(0.5);
    container.add(label);

    bg.on('pointerdown', onClick);
    bg.on('pointerover', () => bg.setFillStyle(Phaser.Display.Color.ValueToColor(color).lighten(20).color));
    bg.on('pointerout', () => bg.setFillStyle(color));

    return container;
  }

  private createSmallButton(x: number, y: number, text: string, color: number, onClick: () => void, width?: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    const w = width ?? Math.max(50, text.length * 7);
    const bg = this.add.rectangle(0, 0, w, 25, color);
    bg.setInteractive({ useHandCursor: true });
    container.add(bg);

    const label = this.add.text(0, 0, text, {
      fontSize: '12px',
      color: '#ffffff',
    });
    label.setOrigin(0.5);
    container.add(label);

    bg.on('pointerdown', () => {
      onClick();
    });

    return container;
  }

  private updateStatus(): void {
    if (!this.currentLevel) return;

    let status = `Tool: ${this.currentTool.toUpperCase()}`;
    
    if (this.hoveredTile) {
      status += ` | Tile: (${this.hoveredTile.x}, ${this.hoveredTile.y})`;
      
      // Show edge indicator
      if (this.isEdgeTile(this.hoveredTile)) {
        status += ' [EDGE - spawn point]';
      }
    }

    if (this.currentTool === 'path' && !this.isDrawingPath) {
      const baseIsPlaced = this.currentLevel.base.x >= 0 && this.currentLevel.base.y >= 0;
      if (baseIsPlaced) {
        status += ' | Click to start path from base';
      } else {
        status += ' | Click to start path (place base first for auto-start)';
      }
    } else if (this.isDrawingPath) {
      status += ` | Drawing: ${this.pathPoints.length} pts - click to add, EDGE to finish`;
    }

    if (this.isDirty) {
      status += ' | [UNSAVED]';
    }

    this.statusText.setText(status);
  }

  // ============================================
  // Wave Builder
  // ============================================

  private createWaveBuilderPanel(): void {
    const panelWidth = 280;
    const panelX = DESIGN_WIDTH - panelWidth - 10;
    
    this.waveBuilderContainer = this.add.container(panelX, MAP_OFFSET_Y);
    this.waveBuilderContainer.setVisible(false);
    this.editorContainer.add(this.waveBuilderContainer);

    // Panel background
    const panelHeight = DESIGN_HEIGHT - MAP_OFFSET_Y - 50;
    const panelBg = this.add.rectangle(panelWidth / 2, panelHeight / 2, panelWidth, panelHeight, 0x2a2a4e, 0.95);
    panelBg.setStrokeStyle(2, 0x4a4a6e);
    this.waveBuilderContainer.add(panelBg);

    // Title
    const title = this.add.text(10, 10, 'WAVE BUILDER', {
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.waveBuilderContainer.add(title);

    // Add Wave button
    const addWaveBtn = this.createSmallButton(panelWidth - 60, 15, '+ WAVE', 0x44aa44, () => {
      this.addWave();
    });
    this.waveBuilderContainer.add(addWaveBtn);

    // Wave list container (scrollable area)
    this.waveListContainer = this.add.container(10, 45);
    this.waveBuilderContainer.add(this.waveListContainer);

    // Enable scroll on wave builder
    this.setupWaveBuilderScroll();
  }

  private setupWaveBuilderScroll(): void {
    // Add mouse wheel scrolling for wave builder
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, gameObjects: Phaser.GameObjects.GameObject[], deltaX: number, deltaY: number) => {
      if (!this.waveBuilderVisible) return;
      
      // Check if pointer is over wave builder panel
      const panelX = DESIGN_WIDTH - 290;
      if (pointer.x >= panelX && pointer.x <= DESIGN_WIDTH - 10 &&
          pointer.y >= MAP_OFFSET_Y && pointer.y <= DESIGN_HEIGHT - 50) {
        this.scrollOffset = Math.max(0, Math.min(this.maxScroll, this.scrollOffset + deltaY * 0.5));
        this.refreshWaveList();
      }
    });
  }

  private toggleWaveBuilder(): void {
    this.waveBuilderVisible = !this.waveBuilderVisible;
    this.waveBuilderContainer.setVisible(this.waveBuilderVisible);
    
    // Hide tower panel if showing wave builder
    if (this.waveBuilderVisible && this.towerPanelVisible) {
      this.towerPanelVisible = false;
      this.towerPanelContainer.setVisible(false);
    }
    
    if (this.waveBuilderVisible) {
      this.refreshWaveList();
    }
  }

  // ============================================
  // Tower Config Panel
  // ============================================

  private createTowerPanel(): void {
    const panelWidth = 280;
    const panelX = DESIGN_WIDTH - panelWidth - 10;
    
    this.towerPanelContainer = this.add.container(panelX, MAP_OFFSET_Y);
    this.towerPanelContainer.setVisible(false);
    this.editorContainer.add(this.towerPanelContainer);

    // Panel background
    const panelHeight = DESIGN_HEIGHT - MAP_OFFSET_Y - 50;
    const panelBg = this.add.rectangle(panelWidth / 2, panelHeight / 2, panelWidth, panelHeight, 0x2a2a4e, 0.95);
    panelBg.setStrokeStyle(2, 0x4a4a6e);
    this.towerPanelContainer.add(panelBg);

    // Title
    const title = this.add.text(10, 10, 'TOWER CONFIG', {
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.towerPanelContainer.add(title);

    // Instructions
    const instructions = this.add.text(10, 35, 'Click to toggle unlock status:', {
      fontSize: '12px',
      color: '#888888',
    });
    this.towerPanelContainer.add(instructions);

    // Legend
    const legendY = 55;
    const legendUnlocked = this.add.text(10, legendY, '● Unlocked', { fontSize: '10px', color: '#44ff44' });
    const legendLocked = this.add.text(80, legendY, '● Locked', { fontSize: '10px', color: '#ffaa44' });
    const legendHidden = this.add.text(140, legendY, '● Hidden', { fontSize: '10px', color: '#ff4444' });
    this.towerPanelContainer.add(legendUnlocked);
    this.towerPanelContainer.add(legendLocked);
    this.towerPanelContainer.add(legendHidden);
  }

  private toggleTowerPanel(): void {
    this.towerPanelVisible = !this.towerPanelVisible;
    this.towerPanelContainer.setVisible(this.towerPanelVisible);
    
    // Hide wave builder if showing tower panel
    if (this.towerPanelVisible && this.waveBuilderVisible) {
      this.waveBuilderVisible = false;
      this.waveBuilderContainer.setVisible(false);
    }
    
    if (this.towerPanelVisible) {
      this.refreshTowerList();
    }
  }

  private refreshTowerList(): void {
    // Clear existing elements
    this.towerPanelElements.forEach(el => el.destroy());
    this.towerPanelElements = [];

    if (!this.currentLevel) return;

    const allTowers = Tower.getAllTypes();
    const allowedTowers = new Set(this.currentLevel.allowedTowers || []);
    const preResearched = new Set(this.currentLevel.preResearchedTowers || []);
    const panelWidth = 260;
    
    let yPos = 80;

    allTowers.forEach((towerType) => {
      const state = this.getTowerState(towerType, allowedTowers, preResearched);
      const towerStats = Tower.getStats(towerType);
      const tier = Tower.getTier(towerType);
      
      const rowContainer = this.add.container(10, yPos);
      this.towerPanelContainer.add(rowContainer);
      this.towerPanelElements.push(rowContainer);

      // Colors based on state
      const colors = {
        unlocked: { bg: 0x2a4a2a, border: 0x44aa44, dot: 0x44ff44 },
        locked: { bg: 0x4a3a2a, border: 0xaa8844, dot: 0xffaa44 },
        hidden: { bg: 0x4a2a2a, border: 0xaa4444, dot: 0xff4444 },
      };
      const c = colors[state];

      // Row background
      const rowBg = this.add.rectangle(panelWidth / 2 - 5, 12, panelWidth - 10, 28, c.bg);
      rowBg.setStrokeStyle(1, c.border);
      rowBg.setInteractive({ useHandCursor: true });
      rowContainer.add(rowBg);

      // Status indicator
      const statusDot = this.add.circle(12, 12, 6, c.dot);
      rowContainer.add(statusDot);

      // Tower name
      const towerName = this.add.text(25, 4, this.formatTowerName(towerType), {
        fontSize: '13px',
        color: state === 'hidden' ? '#888888' : '#ffffff',
        fontStyle: 'bold',
      });
      rowContainer.add(towerName);

      // State label
      const stateLabel = this.add.text(120, 4, state.toUpperCase(), {
        fontSize: '9px',
        color: state === 'unlocked' ? '#44ff44' : (state === 'locked' ? '#ffaa44' : '#ff4444'),
      });
      rowContainer.add(stateLabel);

      // Tier badge
      const tierBadge = this.add.text(panelWidth - 60, 4, `T${tier}`, {
        fontSize: '11px',
        color: '#888888',
      });
      rowContainer.add(tierBadge);

      // Cost
      const costText = this.add.text(panelWidth - 30, 4, `$${towerStats.cost}`, {
        fontSize: '11px',
        color: '#ffdd44',
      });
      rowContainer.add(costText);

      // Click to cycle state: unlocked -> locked -> hidden -> unlocked
      rowBg.on('pointerdown', () => {
        this.cycleTowerState(towerType);
      });

      // Hover effect
      rowBg.on('pointerover', () => {
        rowBg.setAlpha(0.8);
      });
      rowBg.on('pointerout', () => {
        rowBg.setAlpha(1);
      });

      yPos += 32;
    });
  }

  private getTowerState(
    towerType: TowerType, 
    allowedTowers: Set<TowerType>, 
    preResearched: Set<TowerType>
  ): 'unlocked' | 'locked' | 'hidden' {
    if (!allowedTowers.has(towerType)) {
      return 'hidden';
    }
    if (preResearched.has(towerType)) {
      return 'unlocked';
    }
    return 'locked';
  }

  private formatTowerName(towerType: TowerType): string {
    // Capitalize first letter
    return towerType.charAt(0).toUpperCase() + towerType.slice(1);
  }

  private cycleTowerState(towerType: TowerType): void {
    if (!this.currentLevel) return;

    // Initialize preResearchedTowers if not exists
    if (!this.currentLevel.preResearchedTowers) {
      // Default: all allowed towers start as pre-researched (unlocked)
      this.currentLevel.preResearchedTowers = [...this.currentLevel.allowedTowers];
    }

    const allowedIndex = this.currentLevel.allowedTowers.indexOf(towerType);
    const preResearchedIndex = this.currentLevel.preResearchedTowers.indexOf(towerType);
    
    const isAllowed = allowedIndex !== -1;
    const isPreResearched = preResearchedIndex !== -1;

    // Determine current state and cycle to next
    // unlocked (allowed + pre-researched) -> locked (allowed only) -> hidden (neither) -> unlocked
    
    if (isAllowed && isPreResearched) {
      // Currently UNLOCKED -> go to LOCKED (remove from pre-researched)
      this.currentLevel.preResearchedTowers.splice(preResearchedIndex, 1);
    } else if (isAllowed && !isPreResearched) {
      // Currently LOCKED -> go to HIDDEN (remove from allowed)
      // But ensure at least one tower stays unlocked
      const unlockedCount = this.currentLevel.preResearchedTowers.length;
      if (unlockedCount < 1 && this.currentLevel.allowedTowers.length <= 1) {
        alert('You must have at least one tower available.');
        return;
      }
      this.currentLevel.allowedTowers.splice(allowedIndex, 1);
    } else {
      // Currently HIDDEN -> go to UNLOCKED (add to both)
      this.currentLevel.allowedTowers.push(towerType);
      this.currentLevel.preResearchedTowers.push(towerType);
    }

    this.isDirty = true;
    this.refreshTowerList();
  }

  private addWave(): void {
    if (!this.currentLevel) return;

    const newWaveNumber = this.currentLevel.waves.length + 1;
    const defaultPathId = this.currentLevel.paths.length > 0 ? this.currentLevel.paths[0].id : 'path-1';

    const newWave: WaveDef = {
      waveNumber: newWaveNumber,
      spawns: [{
        monsterType: 'crawler',
        count: 10,
        interval: 1.5,
        delay: 0,
        pathId: defaultPathId,
      }],
    };

    this.currentLevel.waves.push(newWave);
    this.isDirty = true;
    this.refreshWaveList();
  }

  private removeWave(index: number): void {
    if (!this.currentLevel) return;

    this.currentLevel.waves.splice(index, 1);
    
    // Renumber remaining waves
    this.currentLevel.waves.forEach((wave, i) => {
      wave.waveNumber = i + 1;
    });

    this.isDirty = true;
    this.selectedWaveIndex = -1;
    this.refreshWaveList();
  }

  private addSpawnToWave(waveIndex: number): void {
    if (!this.currentLevel || waveIndex < 0 || waveIndex >= this.currentLevel.waves.length) return;

    const defaultPathId = this.currentLevel.paths.length > 0 ? this.currentLevel.paths[0].id : 'path-1';
    
    this.currentLevel.waves[waveIndex].spawns.push({
      monsterType: 'crawler',
      count: 10,
      interval: 1.5,
      delay: 0,
      pathId: defaultPathId,
    });

    this.isDirty = true;
    this.refreshWaveList();
  }

  private removeSpawnFromWave(waveIndex: number, spawnIndex: number): void {
    if (!this.currentLevel) return;
    
    const wave = this.currentLevel.waves[waveIndex];
    if (!wave || wave.spawns.length <= 1) return;  // Keep at least one spawn

    wave.spawns.splice(spawnIndex, 1);
    this.isDirty = true;
    this.refreshWaveList();
  }

  private addBossToWave(waveIndex: number): void {
    if (!this.currentLevel || waveIndex < 0 || waveIndex >= this.currentLevel.waves.length) return;
    const wave = this.currentLevel.waves[waveIndex];
    if (wave.boss) return; // Already has boss

    const defaultPathId = this.currentLevel.paths.length > 0 ? this.currentLevel.paths[0].id : 'path-1';
    wave.boss = { type: 'brute_boss', pathId: defaultPathId, delay: 0 };
    this.isDirty = true;
    this.refreshWaveList();
  }

  private removeBossFromWave(waveIndex: number): void {
    if (!this.currentLevel || waveIndex < 0 || waveIndex >= this.currentLevel.waves.length) return;
    const wave = this.currentLevel.waves[waveIndex];
    if (!wave.boss) return;

    delete wave.boss;
    this.isDirty = true;
    this.refreshWaveList();
  }

  private refreshWaveList(): void {
    // Clear existing elements
    this.waveBuilderElements.forEach(el => el.destroy());
    this.waveBuilderElements = [];
    this.waveListContainer.removeAll(true);

    if (!this.currentLevel) return;

    const panelWidth = 260;
    let yPos = -this.scrollOffset;
    const waves = this.currentLevel.waves;

    if (waves.length === 0) {
      const emptyText = this.add.text(0, 10, 'No waves yet.\nClick "+ WAVE" to add one.', {
        fontSize: '14px',
        color: '#888888',
      });
      this.waveListContainer.add(emptyText);
      this.waveBuilderElements.push(emptyText);
      return;
    }

    waves.forEach((wave, waveIndex) => {
      const waveBlockHeight = 50 + wave.spawns.length * 95 + (wave.boss ? 78 : 38);
      if (yPos > DESIGN_HEIGHT - 150) return; // Don't render off-screen
      if (yPos < -200) {
        yPos += waveBlockHeight;
        return;
      }

      // Wave header
      const waveContainer = this.add.container(0, yPos);
      this.waveListContainer.add(waveContainer);
      this.waveBuilderElements.push(waveContainer);

      // Wave header background
      const headerBg = this.add.rectangle(panelWidth / 2, 12, panelWidth, 28, 0x4a4a6e);
      headerBg.setInteractive({ useHandCursor: true });
      waveContainer.add(headerBg);

      // Wave number
      const waveLabel = this.add.text(5, 3, `Wave ${wave.waveNumber}`, {
        fontSize: '14px',
        color: '#ffffff',
        fontStyle: 'bold',
      });
      waveContainer.add(waveLabel);

      // Spawn/boss count badge
      const badgeParts: string[] = [];
      if (wave.spawns.length > 0) badgeParts.push(`${wave.spawns.length} spawn${wave.spawns.length > 1 ? 's' : ''}`);
      if (wave.boss) badgeParts.push('boss');
      const spawnBadge = this.add.text(80, 3, badgeParts.length ? `(${badgeParts.join(', ')})` : '(empty)', {
        fontSize: '12px',
        color: '#aaaaaa',
      });
      waveContainer.add(spawnBadge);

      // Add spawn button
      const addSpawnBtn = this.add.text(panelWidth - 70, 3, '+ Spawn', {
        fontSize: '12px',
        color: '#44ff44',
      });
      addSpawnBtn.setInteractive({ useHandCursor: true });
      addSpawnBtn.on('pointerdown', () => this.addSpawnToWave(waveIndex));
      waveContainer.add(addSpawnBtn);

      // Delete wave button
      const delWaveBtn = this.add.text(panelWidth - 15, 3, 'X', {
        fontSize: '12px',
        color: '#ff4444',
      });
      delWaveBtn.setInteractive({ useHandCursor: true });
      delWaveBtn.on('pointerdown', () => this.removeWave(waveIndex));
      waveContainer.add(delWaveBtn);

      yPos += 30;

      // Spawns
      wave.spawns.forEach((spawn, spawnIndex) => {
        const spawnY = yPos;
        const spawnContainer = this.add.container(0, spawnY);
        this.waveListContainer.add(spawnContainer);
        this.waveBuilderElements.push(spawnContainer);

        // Spawn background
        const spawnBg = this.add.rectangle(panelWidth / 2, 40, panelWidth - 10, 85, 0x3a3a5e);
        spawnBg.setStrokeStyle(1, 0x5a5a7e);
        spawnContainer.add(spawnBg);

        // Monster type dropdown
        const monsterLabel = this.add.text(5, 5, 'Monster:', { fontSize: '11px', color: '#aaaaaa' });
        spawnContainer.add(monsterLabel);
        
        const monsterDropdown = this.createDropdown(
          70, 5, 110, 
          Monster.getAllTypes(),
          spawn.monsterType,
          (value) => {
            spawn.monsterType = value as MonsterType;
            this.isDirty = true;
          }
        );
        spawnContainer.add(monsterDropdown);

        // Delete spawn button (if more than one)
        if (wave.spawns.length > 1) {
          const delSpawnBtn = this.add.text(panelWidth - 20, 5, 'X', {
            fontSize: '11px',
            color: '#ff4444',
          });
          delSpawnBtn.setInteractive({ useHandCursor: true });
          delSpawnBtn.on('pointerdown', () => this.removeSpawnFromWave(waveIndex, spawnIndex));
          spawnContainer.add(delSpawnBtn);
        }

        // Count input
        const countLabel = this.add.text(5, 25, 'Count:', { fontSize: '11px', color: '#aaaaaa' });
        spawnContainer.add(countLabel);
        
        const countInput = this.createNumberInput(70, 25, 50, spawn.count, 1, 100, (value) => {
          spawn.count = value;
          this.isDirty = true;
        });
        spawnContainer.add(countInput);

        // Interval input
        const intervalLabel = this.add.text(130, 25, 'Interval:', { fontSize: '11px', color: '#aaaaaa' });
        spawnContainer.add(intervalLabel);
        
        const intervalInput = this.createNumberInput(185, 25, 50, spawn.interval, 0.1, 10, (value) => {
          spawn.interval = value;
          this.isDirty = true;
        }, 0.1);
        spawnContainer.add(intervalInput);

        // Delay input
        const delayLabel = this.add.text(5, 45, 'Delay:', { fontSize: '11px', color: '#aaaaaa' });
        spawnContainer.add(delayLabel);
        
        const delayInput = this.createNumberInput(70, 45, 50, spawn.delay || 0, 0, 60, (value) => {
          spawn.delay = value;
          this.isDirty = true;
        }, 0.5);
        spawnContainer.add(delayInput);

        // Path dropdown
        const pathLabel = this.add.text(130, 45, 'Path:', { fontSize: '11px', color: '#aaaaaa' });
        spawnContainer.add(pathLabel);
        
        const pathOptions = this.currentLevel?.paths.map(p => p.id) || ['path-1'];
        const pathDropdown = this.createDropdown(
          170, 45, 75,
          pathOptions,
          spawn.pathId,
          (value) => {
            spawn.pathId = value;
            this.isDirty = true;
          }
        );
        spawnContainer.add(pathDropdown);

        yPos += 95;
      });

      // Boss section: show "Add boss" or boss editor (type, path, delay)
      if (wave.boss) {
        const bossY = yPos;
        const bossContainer = this.add.container(0, bossY);
        this.waveListContainer.add(bossContainer);
        this.waveBuilderElements.push(bossContainer);

        const bossBg = this.add.rectangle(panelWidth / 2 - 5, 32, panelWidth - 10, 70, 0x5a3a4e);
        bossBg.setStrokeStyle(1, 0x8a5a6e);
        bossContainer.add(bossBg);

        const bossTitle = this.add.text(5, 3, 'Boss:', { fontSize: '12px', color: '#dd88aa', fontStyle: 'bold' });
        bossContainer.add(bossTitle);

        const bossTypeOptions = BOSS_TYPES.map(b => b.id);
        const bossTypeDropdown = this.createDropdown(
          45, 3, 100,
          bossTypeOptions,
          wave.boss.type,
          (value) => {
            if (wave.boss) wave.boss.type = value;
            this.isDirty = true;
          }
        );
        bossContainer.add(bossTypeDropdown);

        const bossPathLabel = this.add.text(5, 28, 'Path:', { fontSize: '11px', color: '#aaaaaa' });
        bossContainer.add(bossPathLabel);
        const pathOptions = this.currentLevel?.paths.map(p => p.id) || ['path-1'];
        const bossPathDropdown = this.createDropdown(45, 28, 75, pathOptions, wave.boss.pathId, (value) => {
          if (wave.boss) wave.boss.pathId = value;
          this.isDirty = true;
        });
        bossContainer.add(bossPathDropdown);

        const bossDelayLabel = this.add.text(130, 28, 'Delay:', { fontSize: '11px', color: '#aaaaaa' });
        bossContainer.add(bossDelayLabel);
        const bossDelayInput = this.createNumberInput(170, 28, 50, wave.boss.delay ?? 0, 0, 60, (value) => {
          if (wave.boss) wave.boss.delay = value;
          this.isDirty = true;
        }, 0.5);
        bossContainer.add(bossDelayInput);

        const removeBossBtn = this.add.text(panelWidth - 20, 3, 'X', { fontSize: '12px', color: '#ff4444' });
        removeBossBtn.setInteractive({ useHandCursor: true });
        removeBossBtn.on('pointerdown', () => this.removeBossFromWave(waveIndex));
        bossContainer.add(removeBossBtn);

        yPos += 78;
      } else {
        const addBossBtn = this.add.text(5, yPos, '+ Add boss', {
          fontSize: '12px',
          color: '#aa66aa',
        });
        addBossBtn.setInteractive({ useHandCursor: true });
        addBossBtn.on('pointerdown', () => this.addBossToWave(waveIndex));
        this.waveListContainer.add(addBossBtn);
        this.waveBuilderElements.push(addBossBtn);
        yPos += 28;
      }

      yPos += 10; // Gap between waves
    });

    // Calculate max scroll
    this.maxScroll = Math.max(0, yPos + this.scrollOffset - (DESIGN_HEIGHT - MAP_OFFSET_Y - 100));
  }

  private createDropdown(
    x: number, 
    y: number, 
    width: number, 
    options: string[], 
    currentValue: string,
    onChange: (value: string) => void
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    
    // Track current index
    let currentIndex = options.indexOf(currentValue);
    if (currentIndex === -1) currentIndex = 0;

    // Background
    const bg = this.add.rectangle(width / 2, 8, width, 18, 0x4a4a6e);
    bg.setStrokeStyle(1, 0x6a6a8e);
    container.add(bg);

    // Left arrow button
    const leftArrow = this.add.text(-10, 0, '◀', {
      fontSize: '12px',
      color: '#88aaff',
    });
    leftArrow.setInteractive({ useHandCursor: true });
    container.add(leftArrow);

    // Current value text (centered)
    const getDisplayValue = (val: string) => val.length > 12 ? val.substring(0, 12) + '..' : val;
    const valueText = this.add.text(width / 2, 8, getDisplayValue(currentValue), {
      fontSize: '11px',
      color: '#ffffff',
    });
    valueText.setOrigin(0.5);
    container.add(valueText);

    // Right arrow button
    const rightArrow = this.add.text(width + 5, 0, '▶', {
      fontSize: '12px',
      color: '#88aaff',
    });
    rightArrow.setInteractive({ useHandCursor: true });
    container.add(rightArrow);

    // Left arrow - go to previous option
    leftArrow.on('pointerdown', () => {
      currentIndex = (currentIndex - 1 + options.length) % options.length;
      const newValue = options[currentIndex];
      valueText.setText(getDisplayValue(newValue));
      onChange(newValue);
    });

    // Right arrow - go to next option
    rightArrow.on('pointerdown', () => {
      currentIndex = (currentIndex + 1) % options.length;
      const newValue = options[currentIndex];
      valueText.setText(getDisplayValue(newValue));
      onChange(newValue);
    });

    // Hover effects
    leftArrow.on('pointerover', () => leftArrow.setColor('#ffffff'));
    leftArrow.on('pointerout', () => leftArrow.setColor('#88aaff'));
    rightArrow.on('pointerover', () => rightArrow.setColor('#ffffff'));
    rightArrow.on('pointerout', () => rightArrow.setColor('#88aaff'));

    return container;
  }

  private createNumberInput(
    x: number,
    y: number,
    width: number,
    currentValue: number,
    min: number,
    max: number,
    onChange: (value: number) => void,
    step: number = 1
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    // Background
    const bg = this.add.rectangle(width / 2, 8, width, 18, 0x4a4a6e);
    bg.setStrokeStyle(1, 0x6a6a8e);
    container.add(bg);

    // Value text
    const displayValue = step < 1 ? currentValue.toFixed(1) : currentValue.toString();
    const valueText = this.add.text(width / 2, 8, displayValue, {
      fontSize: '11px',
      color: '#ffffff',
    });
    valueText.setOrigin(0.5);
    container.add(valueText);

    // Decrease button
    const decreaseBtn = this.add.text(-8, 1, '-', {
      fontSize: '14px',
      color: '#ff8888',
      fontStyle: 'bold',
    });
    decreaseBtn.setInteractive({ useHandCursor: true });
    decreaseBtn.on('pointerdown', () => {
      const newValue = Math.max(min, currentValue - step);
      const roundedValue = Math.round(newValue * 10) / 10;
      valueText.setText(step < 1 ? roundedValue.toFixed(1) : roundedValue.toString());
      onChange(roundedValue);
      currentValue = roundedValue;
    });
    container.add(decreaseBtn);

    // Increase button
    const increaseBtn = this.add.text(width + 3, 1, '+', {
      fontSize: '14px',
      color: '#88ff88',
      fontStyle: 'bold',
    });
    increaseBtn.setInteractive({ useHandCursor: true });
    increaseBtn.on('pointerdown', () => {
      const newValue = Math.min(max, currentValue + step);
      const roundedValue = Math.round(newValue * 10) / 10;
      valueText.setText(step < 1 ? roundedValue.toFixed(1) : roundedValue.toString());
      onChange(roundedValue);
      currentValue = roundedValue;
    });
    container.add(increaseBtn);

    return container;
  }

  // ============================================
  // Test Options
  // ============================================

  private showTestOptions(): void {
    // Create a simple popup with test options
    const overlay = this.add.rectangle(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, DESIGN_WIDTH, DESIGN_HEIGHT, 0x000000, 0.5);
    overlay.setInteractive();
    overlay.setDepth(999);

    const popup = this.add.container(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2);
    popup.setDepth(1000);

    // Popup background
    const popupBg = this.add.rectangle(0, 0, 300, 200, 0x2a2a4e);
    popupBg.setStrokeStyle(2, 0x6a6a8e);
    popup.add(popupBg);

    // Title
    const title = this.add.text(0, -70, 'TEST OPTIONS', {
      fontSize: '20px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    popup.add(title);

    // Description
    const desc = this.add.text(0, -35, 'Choose how to test your map:', {
      fontSize: '14px',
      color: '#aaaaaa',
    });
    desc.setOrigin(0.5);
    popup.add(desc);

    // Test with Waves button
    const waveCount = this.currentLevel?.waves.length || 0;
    const wavesBtn = this.createButton(0, 10, `WAVES (${waveCount})`, 0x4488ff, () => {
      this.testWithWaves = true;
      this.cleanupTestPopup(overlay, popup);
      this.testLevel();
    });
    wavesBtn.setScale(1.2);
    popup.add(wavesBtn);

    // Test with Experiment mode button
    const expBtn = this.createButton(0, 60, 'EXPERIMENT', 0x44aa44, () => {
      this.testWithWaves = false;
      this.cleanupTestPopup(overlay, popup);
      this.testLevel();
    });
    expBtn.setScale(1.2);
    popup.add(expBtn);

    // Cancel button
    const cancelBtn = this.add.text(120, -80, 'X', {
      fontSize: '20px',
      color: '#ff4444',
      fontStyle: 'bold',
    });
    cancelBtn.setOrigin(0.5);
    cancelBtn.setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerdown', () => {
      this.cleanupTestPopup(overlay, popup);
    });
    popup.add(cancelBtn);

    // Close on overlay click
    overlay.on('pointerdown', () => {
      this.cleanupTestPopup(overlay, popup);
    });
  }

  private cleanupTestPopup(overlay: Phaser.GameObjects.Rectangle, popup: Phaser.GameObjects.Container): void {
    overlay.destroy();
    popup.destroy();
  }

  update(): void {
    InputRouter.lateUpdate();
  }
}
